import {FC, useCallback, useContext, useEffect, useRef, useState} from "react";
import {useLocation, useMatch, useNavigate, useParams} from "react-router-dom";
import {Box, Button, Tab, Tabs, Typography} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {ModuleCompleteButton, ModuleTab} from "@buildwitt/shapeshift-common/v1/module";
import {NotesSection} from "@buildwitt/shapeshift-common/v2/notes";
import {PageTitle} from "@buildwitt/shapeshift-common/v1/pageBase";
import {Files} from "@buildwitt/shapeshift-common/v1/files";
import {CommentsSection} from "@buildwitt/shapeshift-common/v1/comments";
import {Links} from "@buildwitt/shapeshift-common/v1/links";
import {INotificationSeverity, APIState, NotificationContext} from "@buildwitt/shapeshift-identity";
import {CenteredContainer} from "@/components/pageBase/centeredContainer";
import {CurriculumContext} from "@/contexts/curriculumContext";
import {LessonVideoPlayer} from "./LessonVideoPlayer";
import {VideoPlayerRef} from "@buildwitt/shapeshift-common/v2/videoPlayer/videoPlayer";
import {getModule} from "@buildwitt/shapeshift-common/v1/common";
import {ICourseCurriculumModule, ILesson, IModuleEnrollment, SOCKET_EVENTS, LessonVideoProgressPayload} from "@buildwitt/shapeshift-types";
import {getAllFontStyles} from "@/templates/defaultTheme";
import {TrainingfeedContext} from "@/contexts/trainingFeedContext";
import {WYSIWYG} from "@buildwitt/shapeshift-common/v1/wysiwyg";
import {IconDocSearch} from "@/icons/IconDocSearch";
import {IconComments} from "@/icons/IconComments";
import {bitmovinPlayerConfig, palette, toolbarHeight} from "@/constants";
import {Player, PlayerAPI} from "bitmovin-player/modules/bitmovinplayer-core";
import EngineBitmovinModule from "bitmovin-player/modules/bitmovinplayer-engine-bitmovin";
import MseRendererModule from "bitmovin-player/modules/bitmovinplayer-mserenderer";
import HlsModule from "bitmovin-player/modules/bitmovinplayer-hls";
import DashModule from "bitmovin-player/modules/bitmovinplayer-dash";
import AbrModule from "bitmovin-player/modules/bitmovinplayer-abr";
import XmlModule from "bitmovin-player/modules/bitmovinplayer-xml";
import ContainerTSModule from "bitmovin-player/modules/bitmovinplayer-container-ts";
import ContainerMp4Module from "bitmovin-player/modules/bitmovinplayer-container-mp4";
import SubtitlesModule from "bitmovin-player/modules/bitmovinplayer-subtitles";
import SubtitlesCEA608Module from "bitmovin-player/modules/bitmovinplayer-subtitles-cea608";
import PolyfillModule from "bitmovin-player/modules/bitmovinplayer-polyfill";
import StyleModule from "bitmovin-player/modules/bitmovinplayer-style";
import "bitmovin-player/bitmovinplayer-ui.css";
import {UIFactory} from "bitmovin-player-ui";
import {SocketContext} from "@/contexts/socketContext";
import {MiniQuizzer} from "./MiniQuizzer";
import {IconNotes} from "@/icons/IconNotes";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import {PageBaseContext} from "@/contexts/pageBaseContext";
import useEnrollmentActions from "@/hooks/useEnrollmentActions";
import StreakDrawer from "./StreakDrawer/StreakDrawer";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {CompletionCelebrationWrap} from "@/pages/CoursePage/CourseCompletionCelebration/CompletionCelebrationWrap";
import {LocaleContext} from "@/contexts/localeContext";
import {useCourseCurriculum} from "@buildwitt/shapeshift-common/v1/hooks";
import { useSSTranslation } from "@/i18n/useSSTranslation";
import ResumeStreakDrawer from "@/components/dailyStreak/ResumeStreakDrawer";

const useStyles = makeStyles<{ scrollToContinueBannerEnabled: boolean }>()((theme, {scrollToContinueBannerEnabled}) => ({
	miniQuizzer: {
		"[class*='submitBtn']": {
			[theme.breakpoints.down("sm")]: {
				width: "100%"
			}
		}

	},
	resumeVideoContainer: {
		position: "fixed",
		top: toolbarHeight,
		margin: "20px 30px",
		right: "0px",
		zIndex: 30
	},
	title: {
		color: theme.palette.common.white,
		"h2": {
			fontSize: "42px"
		},
		"h5": {
			fontSize: "20px",
			lineHeight: "1.5rem",
			marginBottom: "20px"
		},
		[theme.breakpoints.down("sm")]: {
			marginTop: "0px",
			marginBottom: "0px",
			paddingTop: "0px",
			"h2": {
				fontSize: "32px"
			},
			"h5": {
				fontSize: "16px",
				lineHeight: "1.5rem",
				marginBottom: "0px"
			},
			"&:not(.hasButton) h5": {
				marginBottom: "20px"
			},
			"&.hasButton h5": {
				marginBottom: "0px"
			},
			"&.collapsed h5": {
				display: "-webkit-box",
				WebkitLineClamp: 2,
				WebkitBoxOrient: "vertical",
				overflow: "hidden",
				textOverflow: "ellipsis",
				whiteSpace: "normal"
			}
		}
	},
	centeredContainer: {
		maxWidth: "900px",
		padding: "0 20px",
		paddingBottom: "100px"
	},
	lessonCompleteBtn: {
		width: "200px",
		textTransform: "uppercase",
		[theme.breakpoints.down("sm")]: {
			width: "100% !important",
			float: "none",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			textAlign: "center",
			borderRadius: "999px",
			"&.MuiButton-contained": {
				backgroundColor: theme.palette.primary.main,
				color: theme.palette.common.black,
				boxShadow: `0 0 20px ${theme.palette.primary.main}40, 0 2px 4px rgba(0, 0, 0, 0.2)`
			},
			"&.MuiButton-outlined": {
				backgroundColor: palette.greySolid,
				borderColor: palette.greySolid,
				color: palette.grey60,
				"&:hover": {
					backgroundColor: palette.greySolid,
					borderColor: palette.greySolid
				}
			}
		},
		float: "right"
	},
	content: {
		...getAllFontStyles(theme),
		color: theme.palette.text.primary,
		marginBottom: "40px",
		"& p": {
			lineHeight: 1.5,
			fontSize: "17px"
		},
		"& a": {
			textDecoration: "underline",
			color: theme.palette.primary.main
		}
	},
	viewMoreButton: {
		display: "none",
		[theme.breakpoints.down("sm")]: {
			display: "flex",
			alignItems: "center",
			gap: "4px",
			marginTop: "0px",
			marginBottom: "20px",
			color: theme.palette.common.white,
			opacity: 0.5,
			textTransform: "none",
			padding: 0,
			minWidth: "auto",
			fontSize: "14px",
			background: "transparent",
			boxShadow: "none",
			border: "none",
			transition: "none",
			position: "relative",
			top: "0px",
			"&:hover": {
				background: "transparent",
				boxShadow: "none",
				border: "none"
			},
			"&:focus": {
				outline: "none",
				border: "none"
			},
			"&:active": {
				background: "transparent",
				boxShadow: "none"
			},
			"& .MuiTouchRipple-root": {
				display: "none"
			},
			"& .chevron": {
				transition: "transform 0.2s ease-in-out",
				fontSize: "20px"
			},
			"& .chevron.expanded": {
				transform: "rotate(180deg)"
			}
		}
	},
	contentImage: {
		maxWidth: "100%"
	},
	fileTitle: {
		fontWeight: 700,
		color: theme.palette.common.white,
		margin: "20px 0"
	},
	files: {
		marginBottom: "40px",
		"& > div": {
			background: palette.grey15
		}
	},
	tabs: {
		width: "100%",
		margin: 0,
		marginBottom: "50px",
		"[class*='indicator']": {
			display: "none"
		},
		[theme.breakpoints.up("sm")]: {
			"[class*='MuiTabs-scroller']": {
				marginLeft: "25px"
			}
		},
		[theme.breakpoints.down("sm")]: {
			"[class*='MuiTabs-scroller']": {
				marginLeft: "15px"
			},
			gap: 0,
			marginBottom: "10px"
		}
	},
	activeTab: {
		color: theme.palette.common.white + "!important",
		background: "transparent"
	},
	links: {
		marginBottom: "40px"
	},
	tab: {
		display: "flex",
		fontSize: "14px",
		gap: "5px",
		justifyContent: "space-around",
		color: "#A8A8A8",
		padding: "20px 12px",
		letterSpacing: "0.5px",
		"& svg": {
			marginTop: "4px"
		},
		"& svg path": {
			fill: palette.midGrey
		},
		[theme.breakpoints.down("sm")]: {
			minHeight: "44px",
			fontSize: "13px",
			padding: "0 5px"
		}
	},
	comments: {
		"& > div": {
			background: palette.drawer
		},
		"[class*='MuiAvatar']": {
			background: palette.grey10
		},
		"& *": {
			color: theme.palette.common.white + "!important"
		}
	},
	notes: {
		"& *": {
			color: theme.palette.common.white + "!important"
		},
		"& [class*='inputContainer'] > [class*='MuiTextField']": {
			background: palette.drawer
		},
		"[class*='filtersContainer'] > div": {
			background: palette.grey10
		}
	},
	notesSection: {
		"& [class*='timeStampInputAdornment']": {
			alignSelf: "start",
			marginTop: "5px"
		}
	},
	scrollToContinueBanner: {
		opacity: scrollToContinueBannerEnabled ? 1 : 0,
		transition: "opacity 0.2s ease-in-out",
		position: "absolute",
		color: theme.palette.primary.main,
		width: "100%",
		textAlign: "center",
		paddingTop: "35%",
		height: "100%",
		pointerEvents: "none",
		justifyContent: "center",
		margin: "auto",
		zIndex: scrollToContinueBannerEnabled ? 10 : -1,
		display: scrollToContinueBannerEnabled ? "flex" : "none",
		"& svg": {
			fontSize: 50,
			padding: 0,
			margin: 0,
			[theme.breakpoints.down("sm")]: {
				fontSize: 30
			}
		},
		"& > *": {
			pointerEvents: scrollToContinueBannerEnabled ? "auto" : "none",
			cursor: scrollToContinueBannerEnabled ? "pointer" : "default"
		},
		background: "linear-gradient(to bottom, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.2))"

	}
}));


interface IProps {
	lesson: ILesson
}

export const LessonPageContent: FC<IProps> = ({lesson}) => {
	const {improvedSteaksAndCompletions} = useDevCycleSdk();
	const {trainingfeedState} = useContext(TrainingfeedContext);
	const {t} = useSSTranslation();

	const MODULE_TABS: { label: JSX.Element, value: ModuleTab }[] = [
		{
			label: <><IconDocSearch /> {t("module_tab_overview", "OVERVIEW")}</>,
			value: "overview"
		},
		{
			label: <><IconComments /> {t("module_tab_comments", "COMMENTS")}</>,
			value: "comments"
		},
		{
			label: <><IconNotes /> {t("module_tab_notes", "NOTES")}</>,
			value: "notes"
		}
	];

	const notesTranslations = {
		mostRecent: t("notes.most_recent", "Most recent"),
		oldestFirst: t("notes.oldest_first", "Oldest first"),
		noteInputPlaceholder: t("notes.input_placeholder", "Write your private note")
	};

	const commentsTranslations = {
		loadMore: t("comments.load_more", "Load more"),
		commentInputPlaceholder: t("comments.input_placeholder", "Leave a comment"),
		replyToUser: t("comments.reply_to_user", "Reply to {userName}"),
		replies: t("comments.replies", "replies"),
		today: t("comments.today", "Today"),
		yesterday: t("comments.yesterday", "Yesterday"),
		daysAgo: t("comments.days_ago", "{count} days ago")
	};

	const [videoPlaying, setVideoPlaying] = useState(false);
	const [miniQuizzerDisabled, setMiniQuizzerDisabled] = useState(true);
	const [scrollToContinueBannerEnabled, setScrollToContinueBannerEnabled] = useState(false);
	const {cx, classes} = useStyles({scrollToContinueBannerEnabled});
	const pageIsFeed = useMatch("/feed/:courseSlug/*");
	const {courseSlug} = useParams();
	const {moduleSlug: lessonSlug} = useParams();
	const {curriculumState: curriculum, curriculumDispatch} = useContext(CurriculumContext);
	const [{contentContainerRef, memberedCohort}] = useContext(PageBaseContext);
	const {trainingfeedDispatch} = useContext(TrainingfeedContext);
	const {notify} = useContext(NotificationContext);
	const navigate = useNavigate();
	const [tabSelected, setTab] = useState<ModuleTab>("overview");
	const [firstAvailableModule, setFirstAvailableModule] = useState<ICourseCurriculumModule | false>();
	const videoRef = useRef<VideoPlayerRef | null>(null);
	const buttonScrollRef = useRef<HTMLSpanElement>(null);
	const [player, setPlayer] = useState<PlayerAPI | null>(null);
	(global as any).playerx = player;
	const [videoFinished, setVideoFinished] = useState(false);
	const socket = useContext(SocketContext)
	const location = useLocation();
	const [quizComplete, setQuizComplete] = useState(false);
	const videoContainerRef = useRef<HTMLDivElement | null>(null);
	const {updateLatestLessonCompletedAt} = useEnrollmentActions();
	const [openStreakDrawer, setOpenStreakDrawer] = useState(false);
	const [streakDays, setStreakDays] = useState<number>(0);
	const [{locale}] = useContext(LocaleContext)
	const {course} = useCourseCurriculum(courseSlug, locale)
	const [openCelebration, setOpenCelebration] = useState(false);
	const [pendingStreak, setPendingStreak] = useState<{days: number} | null>(null);
	const [isSubtitleExpanded, setIsSubtitleExpanded] = useState(false);
	const [needsTruncation, setNeedsTruncation] = useState(false);
	const [isStreakPaused, setIsStreakPaused] = useState(false);

	useEffect(() => {
		setVideoFinished(!lesson.videoUrl);
		setQuizComplete(!(lesson as any).quiz);
		setScrollToContinueBannerEnabled(false);
		setIsSubtitleExpanded(false);
	}, [lesson])

	// Check if the subtitle is truncated. Rerun on resize.
	useEffect(() => {
		if (!lesson?.subtitle || !contentContainerRef?.current || isSubtitleExpanded) {
			// Only check truncation when collapsed - when expanded, keep the previous value
			if (!lesson?.subtitle) {
				setNeedsTruncation(false);
			}
			return;
		}

		const MOBILE_BREAKPOINT = 700; // Matches theme.breakpoints.down("sm")

		const checkTruncation = (): void => {
			// Only check on mobile viewport
			if (window.innerWidth >= MOBILE_BREAKPOINT) {
				setNeedsTruncation(false);
				return;
			}

			const container = contentContainerRef?.current;
			if (!container) return;

			const subtitleElement = container.querySelector('[class*="title"] h5') as HTMLElement | null;
			if (!subtitleElement) {
				setNeedsTruncation(false);
				return;
			}

			// Check if content is truncated by comparing scrollHeight to clientHeight
			const isTruncated = subtitleElement.scrollHeight > subtitleElement.clientHeight;
			setNeedsTruncation(isTruncated);
		};

		checkTruncation();
		window.addEventListener("resize", checkTruncation);

		return () => {
			window.removeEventListener("resize", checkTruncation);
		};
	}, [lesson?.subtitle, isSubtitleExpanded]);

	useEffect(() => {
		const updateScrollBanner = (): void => {
			if (!contentContainerRef?.current) return;

			const scrollableDistance = contentContainerRef.current.scrollHeight - contentContainerRef.current.clientHeight;
			const hasScrollDistance = (scrollableDistance > 50);

			setScrollToContinueBannerEnabled(!videoPlaying && !miniQuizzerDisabled && hasScrollDistance && !lesson.complete);
		};

		updateScrollBanner();
		window.addEventListener("resize", updateScrollBanner);

		return () => {
			window.removeEventListener("resize", updateScrollBanner);
		};
	}, [videoPlaying, miniQuizzerDisabled, lesson.complete]);

	useEffect(() => {
		videoRef.current = null;
		if (contentContainerRef?.current) {
			contentContainerRef.current.scrollTo({top: 0, behavior: "smooth"});
		}
	}, [lessonSlug])

	const handleVideoReady = useCallback((lesson: ILesson) => {
		if (!process.env.REACT_APP_BITMOVIN_PLAYER_KEY)
			throw new Error("REACT_APP_BITMOVIN_PLAYER_KEY required")

		if (!videoRef.current?.bitmovin?.container)
			return;

		const playerSource = {
			hls: lesson.videoUrl,
			poster: lesson.videoThumbnailUrl
		};

		Player.addModule(EngineBitmovinModule);
		Player.addModule(MseRendererModule);
		Player.addModule(HlsModule);
		Player.addModule(XmlModule);
		Player.addModule(DashModule);
		Player.addModule(AbrModule);
		Player.addModule(ContainerTSModule);
		Player.addModule(ContainerMp4Module);
		Player.addModule(SubtitlesModule);
		Player.addModule(SubtitlesCEA608Module);
		Player.addModule(PolyfillModule);
		Player.addModule(StyleModule);

		// Delay player setup for Safari
		setTimeout(() => {
			if (!videoRef.current?.bitmovin?.container)
				return;

			const playerInstance = new Player(videoRef.current.bitmovin?.container, bitmovinPlayerConfig);
			UIFactory.buildDefaultUI(playerInstance);
			playerInstance.load(playerSource).then(() => {
				setPlayer(playerInstance);
			}, (e) => {
				console.error("Error while loading source", e);
			});
		}, 100); // Delay added
	}, [])

	useEffect(() => {
		if (courseSlug && lessonSlug && curriculum){
			if(!pageIsFeed)
				setFirstAvailableModule(getModule(curriculum) ?? false)
			else if(trainingfeedState){
				for(const day of ["overdue", ...Object.keys(trainingfeedState)]){
					if(trainingfeedState[day] === "loading"){
						//day still loading, wait and re-run on dep change
						return;
					}
					for(const module of trainingfeedState[day]){
						if(typeof module !== "string" && !module.complete){
							setFirstAvailableModule(module);
							return;
						}
					}
				}
			}
		}
	}, [courseSlug, lessonSlug, curriculum, trainingfeedState])

	const navigateToNext = () => {
		if (pageIsFeed)
			navigate("/feed/");
		else
			navigate("/course/" + courseSlug);
	}

	const handleCompletion = useCallback((e: APIState<IModuleEnrollment>): void => {
		if (!courseSlug)
			return

		if (e.isError) {
			notify("Please try again", "Something went wrong", INotificationSeverity.error, 5000);
		}
		else if (!e.isLoading && !e.isError && e.statusCode === 200) {
			const status = !!e?.data?.complete;
			curriculumDispatch({
				type: "setModuleComplete", module: {type: "lesson", slug: lessonSlug as string}, status
			})

			if (pageIsFeed) {
				trainingfeedDispatch({
					type: "setModuleComplete", module: {courseSlug, type: "lesson", slug: lessonSlug as string}, status
				})
			}

			if (e?.data?.complete) {
				notify(null, "Lesson completed", INotificationSeverity.success, 5000);
				updateLatestLessonCompletedAt(e.data.updatedAt);

				if (e?.data?.isStreakPaused) {
					if (e?.data?.streakDays) {
						setPendingStreak({days: e.data.streakDays});
					}
					setIsStreakPaused(e?.data?.isStreakPaused)
					return;
				}


				const isCourseCompleted =
					course?.modules.filter(module => module.id !== e?.data?.module.id).every(module => module.complete) &&
					course?.modules.find(module => module.id === e?.data?.module.id)?.complete === false;

				if (isCourseCompleted && improvedSteaksAndCompletions) {
					setOpenCelebration(true);

					if (e?.data?.isStreakChanged && e?.data?.streakDays) {
						setPendingStreak({days: e.data.streakDays});
					}
					return;
				} else {
					if(improvedSteaksAndCompletions && e?.data?.isStreakChanged && e?.data?.streakDays){
						setOpenStreakDrawer(true);
						setStreakDays(e?.data?.streakDays);
						return;
					}
				}
				navigateToNext();
			}
			else {
				notify("Set to incomplete", "Lesson Updated", INotificationSeverity.success, 5000);
			}
		}
	}, [curriculumDispatch, lessonSlug, course])

	const handleCompleteCheck = (lesson: ILesson, sendNotification: boolean): boolean => {
		if (lesson.videoUrl && !videoFinished) {
			sendNotification && notify("Please finish the video before progressing", "Hold up!", INotificationSeverity.warning, 5000)
			return false;
		}
		if ((lesson as any).quiz && !quizComplete) {
			sendNotification && notify("Please attempt the quiz before progressing", "Hold up!", INotificationSeverity.warning, 5000)
			return false;
		}

		return true;
	}

	const prevProgress = useRef<number>()
	//BITMOVIN PLAYER PROGRESS
	useEffect(() => {
		if (!player) {
			return;
		}

		//run in loop to save video progress
		const interval = setInterval(() => {
			const started = (player?.getCurrentTime() ?? 0) > 0;
			const finished = (player.getDuration() - player.getCurrentTime()) < 5;
			if(finished) setVideoFinished(true);

			if (started && prevProgress.current !== player.getCurrentTime()) {
				prevProgress.current = player.getCurrentTime();
				const payload: LessonVideoProgressPayload = {progress: player.getCurrentTime(), courseSlug, lessonSlug};
				socket?.emit(SOCKET_EVENTS.LESSON_VIDEO_PROGRESS, payload)
			}

			setVideoPlaying(player.isPlaying())
		}, 500);

		return () => clearInterval(interval);
	}, [player]);

	const handleReactVideoProgress = (e: {playedSeconds: number}): void => {
		if(videoPlaying){
			const payload: LessonVideoProgressPayload = {progress: e.playedSeconds, courseSlug, lessonSlug};
			socket?.emit(SOCKET_EVENTS.LESSON_VIDEO_PROGRESS, payload)
		}
	}

	const handleClickContinue = () => {
		setOpenStreakDrawer(false);
		setStreakDays(0);
	}

	useEffect(() => setMiniQuizzerDisabled(!videoFinished), [videoFinished])
	useEffect(() => (lesson.complete) ? setMiniQuizzerDisabled(false) : undefined, [lesson?.complete])

	const handleCelebrationClose = (needsToContinue: boolean) => {
		setOpenCelebration(false);
		if (pendingStreak) {
			setOpenStreakDrawer(true);
			setStreakDays(pendingStreak.days);
			setPendingStreak(null);
		}else {
			if (pageIsFeed && needsToContinue){
				if (curriculum && curriculum.percentCompleted === 100){
					navigate("/training-feed")
				}else {
					navigateToNext();
				}
			}
		}
	};

	const handleResumeStreakClose = (isStreakResumed: boolean, streak: number) => {
		setIsStreakPaused(false);
		if (pendingStreak && isStreakResumed) {
			setOpenStreakDrawer(true);
			setStreakDays(streak);
			setPendingStreak(null);
		}else {
			if (pageIsFeed){
				if (curriculum && curriculum.percentCompleted === 100){
					navigate("/training-feed")
				}else {
					navigateToNext();
				}
			}
		}
	};

	return (<>
		<Box ref={videoContainerRef}>
			<LessonVideoPlayer
				componentOverrides={{
					header: scrollToContinueBannerEnabled ? <Box
						onClick={() => (contentContainerRef.current && scrollToContinueBannerEnabled) && contentContainerRef.current.scrollBy({top: videoContainerRef.current?.offsetHeight, behavior: "smooth"})}
						className={classes.scrollToContinueBanner}
					>
						<KeyboardDoubleArrowDownIcon /><Typography variant="h5">{t("please_scroll_to_continue", "Please scroll to continue")}</Typography>
					</Box> : null
				}}
				onReady={() => lesson ? handleVideoReady(lesson) : null}
				ref={videoRef}
				lesson={lesson}
				currentTime={location?.state?.timestamp ?? lesson?.videoTime}
				bitmovinPlayer={player}
				onVideoFinished={() => setVideoFinished(true)}
				onPlay={() => setVideoPlaying(true)}
				onPause={() => setVideoPlaying(false)}
				onEnded={() => {setVideoPlaying(false); setVideoFinished(true)}}
				onProgress={handleReactVideoProgress}

			/>
		</Box>
		<Box className={classes.tabs}>
			<Tabs
				variant="standard"
				value={tabSelected}
				onChange={(_e, tab) => setTab(tab)}
			>
				{MODULE_TABS.filter(e => e.value !== "comments" || memberedCohort?.comments).map(({label, value}) => (
					<Tab
						data-test-id={`module-tabs-${value?.toLocaleLowerCase()?.replace(" ","")}`}
						key={value}
						value={value}
						label={label}
						className={cx(classes.tab, tabSelected === value ? classes.activeTab : "")}
					/>
				))}
			</Tabs>
		</Box>
		<CenteredContainer className={classes.centeredContainer}>
			{tabSelected === "overview" && <>
				<PageTitle className={cx(classes.title, !isSubtitleExpanded && "collapsed", needsTruncation && "hasButton")} title={lesson?.title} subtitle={lesson?.subtitle} />
				{lesson?.content && (
					<WYSIWYG className={classes.content}>
						{lesson.content}
					</WYSIWYG>
				)}
				{needsTruncation && (
					<Button
						className={classes.viewMoreButton}
						onClick={() => setIsSubtitleExpanded(!isSubtitleExpanded)}
						disableRipple
						disableTouchRipple
						aria-expanded={isSubtitleExpanded}
						aria-label={isSubtitleExpanded ? t("view_less", "View less") : t("view_more", "View more")}
					>
						{isSubtitleExpanded ? t("view_less", "View less") : t("view_more", "View more")}
						<ExpandMoreIcon className={cx("chevron", isSubtitleExpanded && "expanded")} aria-hidden="true" />
					</Button>
				)}

				{(lesson as any)?.quiz &&
						<MiniQuizzer
							disabledMessage="Please complete the video before taking the quiz"
							disabled={miniQuizzerDisabled}
							autoStart
							className={classes.miniQuizzer}
							onComplete={() => {
								setQuizComplete(true)
							}}
							quiz={(lesson as any).quiz}
							onQuizResult={e => {{
								setQuizComplete(!!e)
								const el = contentContainerRef.current;
								if(scrollToContinueBannerEnabled && el)
									setTimeout(() => {
										el.scrollTo({top: el.scrollHeight, behavior: "smooth"});
									}, 300);
							}}}
						/>
				}

				{lesson?.files && <>
					<Typography className={classes.fileTitle} variant="h5">Files</Typography>
					<Files className={classes.files} files={lesson.files} />
				</>}

				{lesson?.links && lesson.links.length > 0 && <>
					<Typography className={classes.fileTitle} variant="h5">Links</Typography>
					<Links className={classes.links} links={lesson.links} />
				</>}
				<span ref={buttonScrollRef} />
				{courseSlug && lesson?.slug && !lesson?.complete &&
					<ModuleCompleteButton
						style={{opacity: handleCompleteCheck(lesson, false) ? 1 : 0.5, width: 'auto'}}
						validate={() => handleCompleteCheck(lesson, true)}
						courseSlug={courseSlug}
						moduleSlug={lesson.slug}
						type="lesson"
						className={classes.lessonCompleteBtn}
						onApiUpdate={handleCompletion}
						label={t("mark_as_completed", "Mark as completed")}
						variant={handleCompleteCheck(lesson, false) ? "contained" : "outlined"}
					>
						{() => {
							const isDisabled = !handleCompleteCheck(lesson, false);
							return (
								<>
									{t("mark_as_completed", "Mark as completed")}
									{isDisabled && <LockIcon sx={{marginLeft: "8px", fontSize: "16px"}} />}
								</>
							);
						}}
					</ModuleCompleteButton>
				}
				{courseSlug && lesson?.slug && lesson?.complete && firstAvailableModule &&
						<Button
							onClick={() => navigate(pageIsFeed
								? "/feed/"
								: `/course/${courseSlug}/${firstAvailableModule.type}/${firstAvailableModule?.slug}`
							)}
							className={classes.lessonCompleteBtn}
						>
							Next {firstAvailableModule ? firstAvailableModule?.type : ""}
						</Button>
				}
			</>}
			{tabSelected === "comments" && <CommentsSection className={classes.comments} courseSlug={courseSlug} moduleType={"lesson"} moduleSlug={lessonSlug} translations={commentsTranslations} />}
			{tabSelected === "notes" && <NotesSection className={classes.notesSection} disableTimeStamp={!lesson?.videoUrl} courseSlug={courseSlug} moduleType="lesson" moduleSlug={lessonSlug} videoRef={videoRef} translations={notesTranslations} onGoToModuleAction={(courseId, moduleId, moduleType, timestamp) => { navigate(`/course/${courseId}/${moduleType}/${moduleId}`, {state: {timestamp}}) }} />}
			<StreakDrawer
				open={openStreakDrawer && streakDays > 0}
				streakDays={streakDays}
				onContinue={handleClickContinue}
			/>
			<ResumeStreakDrawer
				open={isStreakPaused}
				onContinue={handleResumeStreakClose}
			/>
		</CenteredContainer>
		<CompletionCelebrationWrap
			open={openCelebration}
			onClose={handleCelebrationClose}
		/>
	</>
	);
}